"use client"

import { ChevronLeft, ChevronRight, Expand, X } from 'lucide-react'
import { useEffect, useState } from 'react';
import { ImageLoader } from '../ui/image-loader';
import { cloudFrontUrl } from '@/lib/utils';

interface DestinationGalleryProps {
    relatedImages: string[];
}

const DestinationGallery = ({ relatedImages }: DestinationGalleryProps) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

    const openImagePopup = (index: number) => {
        setSelectedImageIndex(index);
    };

    const closeImagePopup = () => {
        setSelectedImageIndex(null);
    };

    const navigateImages = (direction: 'prev' | 'next') => {
        setSelectedImageIndex((prevIndex) => {
            if (prevIndex === null) return null;
            const newIndex =
                direction === 'prev'
                    ? (prevIndex - 1 + relatedImages.length) % relatedImages.length
                    : (prevIndex + 1) % relatedImages.length;
            return newIndex;
        });
    };

    useEffect(() => {
        return () => {
            setSelectedImageIndex(null);
        };
    }, []);

    return (
        <div className='h-40 md:h-80 lg:h-[27rem] xl:h-[30rem] w-full'>
            <div className='grid grid-cols-4 grid-rows-2 h-full w-full [&>*:nth-child(1)]:col-span-2 [&>*:nth-child(1)]:row-span-2 gap-2 md:gap-4 xl:gap-6'>
                {relatedImages.slice(0, 5).map((url, i) => (
                    <div key={i} className='relative h-full w-full group cursor-pointer' onClick={() => openImagePopup(i)}>
                        <ImageLoader src={cloudFrontUrl(url)} alt={`Image ${i + 1}`} fill className='object-cover rounded-md md:rounded-xl' />
                        <div className='group-hover:bg-[#00A6A6]/35 h-full w-full rounded-md md:rounded-xl relative transition duration-500' />
                        <Expand className='md:size-8 size-5 text-white scale-0 group-hover:scale-100 transition duration-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' strokeWidth={1.6} />
                    </div>
                ))}
            </div>
            {selectedImageIndex !== null && (
                <div className="fixed z-40 top-0 left-0 w-full h-full flex items-center justify-center backdrop-blur-sm bg-custom-primary/35" aria-modal="true" role="dialog">
                    <div className="relative h-full w-full">
                        <button className="absolute top-2 right-2 text-custom-primary-dark bg-white rounded-full h-8 w-8 p-1" onClick={closeImagePopup} aria-label="Close">
                            <X className='h-full w-full' />
                        </button>
                        <button
                            className="absolute top-1/2 left-1/4 text-custom-primary bg-white rounded-full h-8 w-8 p-1 -ml-4 -mt-2"
                            onClick={() => navigateImages('prev')}
                            aria-label="Previous Image"
                        >
                            <ChevronLeft />
                        </button>
                        <button
                            className="absolute top-1/2 right-1/4 text-custom-primary bg-white rounded-full h-8 w-8 p-1 -mr-4 -mt-2"
                            onClick={() => navigateImages('next')}
                            aria-label="Next Image"
                        >
                            <ChevronRight />
                        </button>
                        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60vh] w-11/12 min-w-[260px] max-w-2xl z-50'>
                            <ImageLoader
                                src={relatedImages[selectedImageIndex] ? cloudFrontUrl(relatedImages[selectedImageIndex]) : ''}
                                alt={`Image ${selectedImageIndex + 1}`}
                                className="object-contain rounded-xl"
                                fill
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DestinationGallery
